/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect } from "react";
import { apiClient, useAuth } from "../App";
import { useAdminControls } from "../AdminMode";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { ScrollArea } from "../components/ui/scroll-area";
import { format } from "date-fns";
import { toast } from "sonner";
import { 
  BarChart3, 
  Users, 
  Calendar, 
  TrendingUp, 
  Check, 
  X, 
  HelpCircle,
  Clock,
  Eye,
  Download,
  Percent
} from "lucide-react";
import SortableTableHead from "../components/SortableTableHead";
import useTableControls from "../hooks/useTableControls";
import useScrollRestoration from "../hooks/useScrollRestoration";

// Iter-69: column config for sort + per-column filter dropdowns.
const ATTENDANCE_COLUMNS = [
  { key: "player_name", label: "Player", accessor: "player_name", sortable: true, filterable: false },
  { key: "section_name", label: "Section", accessor: "section_name", sortable: true, filterable: true, filterFormatter: (v) => v || "Unassigned" },
  { key: "attended", label: "Attended", accessor: (r) => Number(r.attended || 0), sortable: true, filterable: false },
  { key: "not_attended", label: "Absent", accessor: (r) => Number(r.not_attended || 0), sortable: true, filterable: false },
  { key: "maybe", label: "Maybe", accessor: (r) => Number(r.maybe || 0), sortable: true, filterable: false },
  { key: "no_response", label: "No Response", accessor: (r) => Number(r.no_response || 0), sortable: true, filterable: false },
  { key: "attendance_rate", label: "Attendance Rate", accessor: (r) => Number(r.attendance_rate || 0), sortable: true, filterable: false },
  // iter-107i: Days since the player's freshest RSVP. Big number = silent
  // member. Sortable so admins can find the coldest contacts in one click.
  { key: "last_rsvp_at", label: "Last RSVP", accessor: (r) => r.last_rsvp_at ? new Date(r.last_rsvp_at).getTime() : 0, sortable: true, filterable: false },
];

// Iter-70: column config for the Event Attendance tab.
const EVENT_COLUMNS = [
  { key: "event_title", label: "Event", accessor: "event_title", sortable: true, filterable: false },
  { key: "event_date", label: "Date", accessor: (r) => r.event_date ? new Date(r.event_date) : null, sortable: true, filterable: false },
  { key: "event_type", label: "Type", accessor: "event_type", sortable: true, filterable: true },
  { key: "attended", label: "Attended", accessor: (r) => Number(r.attended || 0), sortable: true, filterable: false },
  { key: "not_attended", label: "Absent", accessor: (r) => Number(r.not_attended || 0), sortable: true, filterable: false },
  { key: "deps_confirmed", label: "Deps", accessor: (r) => Number(r.deps_confirmed || 0), sortable: true, filterable: false },
  { key: "attendance_rate", label: "Attendance Rate", accessor: (r) => Number(r.attendance_rate || 0), sortable: true, filterable: false },
];

const Reports = () => {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [playerStats, setPlayerStats] = useState([]);
  const [eventStats, setEventStats] = useState([]);
  const [loading, setLoading] = useState(true);
  // iter-86: preserve scroll position on detail navigation
  useScrollRestoration({ ready: !loading });
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerHistory, setPlayerHistory] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);

  const isAdmin = user?.role === "admin";
  const { showAdminControls } = useAdminControls();

  // Iter-69: sort + per-column filter state for the player attendance table.
  const playerCtl = useTableControls(playerStats, ATTENDANCE_COLUMNS);
  // Iter-70: Event Attendance table — default sort by Date descending
  // (newest first) per user request.
  const eventCtl = useTableControls(eventStats, EVENT_COLUMNS, { defaultSortKey: "event_date", defaultSortDir: "desc" });

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const [summaryRes, playerRes, eventRes] = await Promise.all([
        apiClient.get("/reports/attendance-summary"),
        apiClient.get("/reports/player-attendance"),
        apiClient.get("/reports/event-attendance")
      ]);
      setSummary(summaryRes.data);
      setPlayerStats(playerRes.data);
      setEventStats(eventRes.data);
    } catch (error) {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  const fetchPlayerHistory = async (playerId, eventType = "") => {
    try {
      const url = eventType
        ? `/reports/attendance-history/${playerId}?event_type=${encodeURIComponent(eventType)}`
        : `/reports/attendance-history/${playerId}`;
      const res = await apiClient.get(url);
      setPlayerHistory(res.data);
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load player history");
    }
  };

  const getAttendanceColor = (rate) => {
    if (rate >= 80) return "text-green-400";
    if (rate >= 60) return "text-yellow-400";
    if (rate >= 40) return "text-orange-400";
    return "text-red-400";
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "attending":
        return <Badge className="bg-green-500/20 text-green-400"><Check className="w-3 h-3 mr-1" />Attended</Badge>;
      case "not_attending":
        return <Badge className="bg-red-500/20 text-red-400"><X className="w-3 h-3 mr-1" />Absent</Badge>;
      case "maybe":
        return <Badge className="bg-yellow-500/20 text-yellow-400"><HelpCircle className="w-3 h-3 mr-1" />Maybe</Badge>;
      default:
        return <Badge className="bg-muted text-muted-foreground"><Clock className="w-3 h-3 mr-1" />No Response</Badge>;
    }
  };

  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) return;
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(h => {
        const val = row[h];
        if (val instanceof Date) return format(val, "yyyy-MM-dd");
        if (typeof val === 'string' && val.includes(',')) return `"${val}"`;
        return val ?? '';
      }).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success(`Exported ${filename}.csv`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-primary text-xl font-heading">Loading reports...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Attendance Reports</h1>
          <p className="text-muted-foreground mt-1">Track attendance patterns and statistics</p>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Events</p>
                  <p className="text-3xl font-heading font-bold mt-1">{summary.total_events}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Members</p>
                  <p className="text-3xl font-heading font-bold mt-1">{summary.total_members}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Avg Attendance</p>
                  <p className={`text-3xl font-heading font-bold mt-1 ${getAttendanceColor(summary.average_attendance_rate)}`}>
                    {summary.average_attendance_rate}%
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <Percent className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Attended</p>
                  <p className="text-3xl font-heading font-bold mt-1 text-green-400">{summary.status_counts.attending}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <Check className="w-6 h-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Absent</p>
                  <p className="text-3xl font-heading font-bold mt-1 text-red-400">{summary.status_counts.not_attending}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-red-500/10 flex items-center justify-center">
                  <X className="w-6 h-6 text-red-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="players" className="w-full">
        <TabsList className="bg-secondary/50 mb-4">
          <TabsTrigger value="players" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Users className="w-4 h-4 mr-2" />
            By Player
          </TabsTrigger>
          <TabsTrigger value="events" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Calendar className="w-4 h-4 mr-2" />
            By Event
          </TabsTrigger>
        </TabsList>

        {/* Player Stats Tab */}
        <TabsContent value="players">
          <Card className="glass-card">
            <CardHeader className="border-b border-white/5 pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg">Player Attendance Statistics</CardTitle>
                {showAdminControls && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => exportToCSV(playerStats, 'player-attendance')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {playerStats.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      {ATTENDANCE_COLUMNS.map((col) => (
                        <SortableTableHead
                          key={col.key}
                          column={col}
                          sortKey={playerCtl.sortKey}
                          sortDir={playerCtl.sortDir}
                          onToggleSort={() => playerCtl.toggleSort(col.key)}
                          rows={playerStats}
                          selectedFilters={playerCtl.filters[col.key]}
                          onChangeFilter={(set) => playerCtl.setColumnFilter(col.key, set)}
                          className={["attended","not_attended","maybe","no_response"].includes(col.key) ? "text-center" : undefined}
                        />
                      ))}
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {playerCtl.processedRows.map((player) => (
                      <TableRow key={player.player_id} className="border-white/5">
                        <TableCell className="font-medium">{player.player_name}</TableCell>
                        <TableCell>
                          {player.section_name ? (
                            <Badge variant="outline" className="border-primary/30 text-primary">
                              {player.section_name}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center text-green-400">{player.attended}</TableCell>
                        <TableCell className="text-center text-red-400">{player.not_attended}</TableCell>
                        <TableCell className="text-center text-yellow-400">{player.maybe}</TableCell>
                        <TableCell className="text-center text-muted-foreground">{player.no_response}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Progress 
                              value={player.attendance_rate} 
                              className="w-20 h-2"
                            />
                            <span className={`text-sm font-medium ${getAttendanceColor(player.attendance_rate)}`}>
                              {player.attendance_rate}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {(() => {
                            if (!player.last_rsvp_at) {
                              return <span className="text-muted-foreground">never</span>;
                            }
                            const days = Math.floor(
                              (Date.now() - new Date(player.last_rsvp_at).getTime()) / 86400000
                            );
                            // Colour code: fresh (<14d) green, stale (14-60d)
                            // amber, cold (>60d) red.
                            const tone =
                              days < 14
                                ? "text-green-400"
                                : days < 60
                                  ? "text-amber-400"
                                  : "text-red-400";
                            const label =
                              days === 0
                                ? "today"
                                : days === 1
                                  ? "1 day ago"
                                  : `${days} days ago`;
                            return (
                              <span className={tone} title={format(new Date(player.last_rsvp_at), "d MMM yyyy, HH:mm")}>
                                {label}
                              </span>
                            );
                          })()}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => fetchPlayerHistory(player.player_id)}
                            data-testid={`view-history-${player.player_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No attendance data available yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Event Stats Tab */}
        <TabsContent value="events">
          <Card className="glass-card">
            <CardHeader className="border-b border-white/5 pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="font-heading text-lg">Event Attendance Statistics</CardTitle>
                {showAdminControls && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => exportToCSV(eventStats.map(e => ({
                      ...e,
                      event_date: e.event_date ? format(new Date(e.event_date), "yyyy-MM-dd") : ''
                    })), 'event-attendance')}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {eventStats.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/5 hover:bg-transparent">
                      {EVENT_COLUMNS.map((col) => (
                        <SortableTableHead
                          key={col.key}
                          column={col}
                          sortKey={eventCtl.sortKey}
                          sortDir={eventCtl.sortDir}
                          onToggleSort={() => eventCtl.toggleSort(col.key)}
                          rows={eventStats}
                          selectedFilters={eventCtl.filters[col.key]}
                          onChangeFilter={(set) => eventCtl.setColumnFilter(col.key, set)}
                          className={["attended","not_attended","deps_confirmed"].includes(col.key) ? "text-center" : undefined}
                        />
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {eventCtl.processedRows.map((event) => (
                      <TableRow key={event.event_id} className="border-white/5">
                        <TableCell className="font-medium">{event.event_title}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {event.event_date ? format(new Date(event.event_date), "MMM d, yyyy") : "—"}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            event.event_type === 'concert' 
                              ? 'border-velvet-red/30 text-velvet-red' 
                              : 'border-blue-400/30 text-blue-400'
                          }>
                            {event.event_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center text-green-400">{event.attended}</TableCell>
                        <TableCell className="text-center text-red-400">{event.not_attended}</TableCell>
                        <TableCell className="text-center text-blue-400">{event.deps_confirmed}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Progress 
                              value={event.attendance_rate} 
                              className="w-20 h-2"
                            />
                            <span className={`text-sm font-medium ${getAttendanceColor(event.attendance_rate)}`}>
                              {event.attendance_rate}%
                            </span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No events recorded yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Player History Dialog — per-player concert-by-concert breakdown */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="glass border-white/10 max-w-3xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl" data-testid="player-history-title">
              Attendance breakdown: {playerHistory?.player_name || ""}
            </DialogTitle>
          </DialogHeader>
          {playerHistory && (
            <>
              {/* iter-107h: totals strip + event-type filter chips */}
              <div className="flex flex-wrap items-center gap-3 pb-3 border-b border-white/5">
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="px-2.5 py-1 rounded-full bg-green-500/15 text-green-300">
                    Attending {playerHistory.totals?.attending ?? 0}
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-red-500/15 text-red-300">
                    Not attending {playerHistory.totals?.not_attending ?? 0}
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-200">
                    Maybe {playerHistory.totals?.maybe ?? 0}
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-white/5 text-muted-foreground">
                    No response {playerHistory.totals?.no_response ?? 0}
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-white/5 text-muted-foreground">
                    Total events {playerHistory.totals?.total_events ?? 0}
                  </span>
                </div>
                <div className="ml-auto flex gap-1 text-xs">
                  {[
                    { label: "All", value: "" },
                    { label: "Concerts", value: "concert" },
                    { label: "Rehearsals", value: "rehearsal" },
                  ].map((opt) => (
                    <button
                      key={opt.value || "all"}
                      type="button"
                      onClick={() => fetchPlayerHistory(playerHistory.player_id, opt.value)}
                      className={
                        "px-2.5 py-1 rounded-full border transition "
                        + ((playerHistory.filter?.event_type || "") === opt.value
                          ? "bg-primary/20 border-primary/40 text-primary"
                          : "border-white/10 text-muted-foreground hover:border-white/30")
                      }
                      data-testid={`player-history-filter-${opt.value || "all"}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <ScrollArea className="h-[500px] pr-4 mt-2">
                <div className="space-y-2">
                  {playerHistory.history.length === 0 && (
                    <p className="text-sm text-muted-foreground py-8 text-center">
                      No events match this filter.
                    </p>
                  )}
                  {playerHistory.history.map((event) => (
                    <div
                      key={event.event_id}
                      className="p-3 rounded-lg bg-secondary/30 space-y-2"
                      data-testid={`player-history-row-${event.event_id}`}
                    >
                      {/* Status + updated timestamp on top so they're
                          always visible on mobile, regardless of how
                          long the event title runs. */}
                      <div className="flex items-center justify-between gap-2">
                        {getStatusBadge(event.status)}
                        {event.updated_at && (
                          <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                            updated {format(new Date(event.updated_at), "d MMM, HH:mm")}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="font-medium leading-snug">{event.event_title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {event.event_date ? format(new Date(event.event_date), "EEE d MMM yyyy") : "—"}
                          {" · "}
                          <span className="capitalize">{event.event_type}</span>
                          {event.location ? ` · ${event.location}` : ""}
                        </p>
                        {event.notes && (
                          <p className="text-xs italic text-muted-foreground mt-1">
                            &ldquo;{event.notes}&rdquo;
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Reports;
